// ============================================================================
// src/app/navigation/appNavigate.ts — the facade's imperative navigation hook.
// ============================================================================
// WHY THIS EXISTS
// B1 places every imperative navigation call behind one typed seam so the
// future router cutover has exactly one non-root owner of the navigation
// contract. The handler is a thin, behavior-preserving wrapper over the
// existing react-router navigate function:
//
//   * destinations are internal absolute path strings — every measured call
//     site in the tree already navigates this way (163/163), so enforcing it
//     changes no current behavior and makes any future drift loud. Relative,
//     protocol-relative ('//…'), scheme/authority, control-character,
//     whitespace, backslash, and non-string destinations are refused;
//   * options are read ONLY through own data descriptors: the object must be
//     plain (prototype Object.prototype or null), never an array, class
//     instance, or proxy with accessor-shaped descriptors; getters and symbol
//     keys refuse the call. `replace` is absent or a strict boolean; `state`
//     passes normalizeNavigationState and a hostile payload REFUSES the
//     navigation (bounded TypeError) — it never silently becomes a plausible
//     state:null navigation. Unknown keys are stripped, but only after the
//     object is proven plain and descriptor-safe;
//   * numbers are never accepted here — delta movement is a separate explicit
//     surface (appHistory.js), and zero current call sites use it.
//
// createAppNavigateHandler is the pure factory (the injected navigate is the
// only collaborator) so the contract is unit-testable without mounting React;
// useAppNavigate is the hook binding over it.
// ============================================================================

import {useMemo} from 'react';
import type {AppNavigateOptions, NavigateFn} from './navigationDriver.js';
import {resolveNavigationDriver} from './navigationDriver.js';
import {normalizeNavigationState} from './navigationState.js';

// The handler this module hands out. Numbers are absent from `to` on purpose —
// delta movement is appHistory's surface, never this one.
export type AppNavigate = (to: string, options?: AppNavigateOptions) => void;

// Destination safety: internal absolute paths only — '/'-leading, never
// protocol-relative, and no whitespace, control characters, or backslashes.
// The character check avoids a control-character regex (no-control-regex).
function hasForbiddenDestinationChar(value: string): boolean {
  for (const ch of value) {
    if (ch < ' ' || ch === '\u007F' || ch === ' ' || ch === '\\') return true;
  }
  return false;
}

// `to` is `unknown`: AppLink hands over whatever a caller put in the prop, and
// refusing a non-string destination is half of what this check is for.
export function assertInternalAbsolutePath(to: unknown, caller: string): void {
  if (typeof to !== 'string' || to.length === 0 || !to.startsWith('/') || to.startsWith('//')) {
    throw new TypeError(`${caller} destinations must be internal absolute path strings; received ${String(to)}`);
  }
  if (hasForbiddenDestinationChar(to)) {
    throw new TypeError(`${caller} destinations must not contain whitespace, control characters, or backslashes`);
  }
}

// One own data property, as proven by its descriptor. `present` is what the
// caller branches on; `value` stays `unknown` because nothing has validated it
// yet.
interface DescriptorRead {
  present: boolean;
  value: unknown;
}

// Read the options object through own data descriptors only. Returns the
// proven-safe descriptor values {hasReplace, replace, hasState, state}; throws
// a bounded TypeError on anything that is not a plain, accessor-free,
// symbol-free, own-data object. No [[Get]] ever runs on the caller's object.
function readOptionsDescriptors(options: unknown): {replace: DescriptorRead; state: DescriptorRead} {
  if (options === null || typeof options !== 'object') {
    throw new TypeError('appNavigate options must be a plain object ({replace, state}) when provided');
  }
  let descriptors: Record<string, PropertyDescriptor>;
  let isArray: boolean;
  try {
    isArray = Array.isArray(options); // throws on a revoked proxy
    if (!isArray) {
      const proto = Object.getPrototypeOf(options); // throws on a revoked proxy
      if (proto !== Object.prototype && proto !== null) {
        throw new TypeError('appNavigate options must be a plain object, not a class instance');
      }
    }
    descriptors = Object.getOwnPropertyDescriptors(options);
  } catch (error) {
    if (error instanceof TypeError && error.message.startsWith('appNavigate')) throw error;
    throw new TypeError('appNavigate options could not be inspected; refusing the navigation');
  }
  if (isArray) {
    throw new TypeError('appNavigate options must be a plain object, not an array');
  }
  const read = (key: string): DescriptorRead => {
    const descriptor = descriptors[key];
    if (!descriptor) return {present: false, value: undefined};
    if (typeof descriptor.get === 'function' || typeof descriptor.set === 'function') {
      throw new TypeError(`appNavigate options.${key} must be an own data property, not an accessor`);
    }
    return {present: true, value: descriptor.value};
  };
  for (const key of Reflect.ownKeys(descriptors)) {
    if (typeof key === 'symbol') throw new TypeError('appNavigate options must not carry symbol keys');
  }
  return {replace: read('replace'), state: read('state')};
}

export function createAppNavigateHandler(navigate: NavigateFn): AppNavigate {
  if (typeof navigate !== 'function') {
    throw new TypeError('createAppNavigateHandler requires the react-router navigate function');
  }
  return function appNavigate(to: string, options?: AppNavigateOptions): void {
    assertInternalAbsolutePath(to, 'appNavigate');
    if (options === undefined) {
      navigate(to);
      return;
    }
    const read = readOptionsDescriptors(options);
    const next: AppNavigateOptions = {};
    if (read.replace.present) {
      if (typeof read.replace.value !== 'boolean') {
        throw new TypeError('appNavigate options.replace must be a strict boolean when provided');
      }
      next.replace = read.replace.value;
    }
    if (read.state.present) {
      const normalized = normalizeNavigationState(read.state.value);
      if (!normalized.ok) {
        // Refuse, loudly and in bounded terms: a hostile or unsupported state
        // never becomes a plausible navigation carrying silently-cleared state.
        throw new TypeError(`appNavigate state is not a safe navigation payload (${normalized.reason})`);
      }
      next.state = normalized.value;
    }
    navigate(to, next);
  };
}

export function useAppNavigate(): AppNavigate {
  const navigate = resolveNavigationDriver().useNavigateFn();
  return useMemo(() => createAppNavigateHandler(navigate), [navigate]);
}
